import { Injectable } from '@angular/core';
import * as authenticationApi from '@authentication-api';
import { Observable, of, repeat } from 'rxjs';
import { RegistrationResult } from '@qvs/core';
import { catchError, exhaustMap, last, map, takeWhile } from 'rxjs/operators';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';

export interface RegistrationRequest {
  contractnumber: string;
  initialPassphrase: string;
}

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private readonly statusCheckInterval = 500;
  private readonly maxStatusChecksBeforeTimeout = 120;
  constructor(private readonly nevisService: authenticationApi.NevisService) {}

  register(request: RegistrationRequest): Observable<RegistrationResult> {
    return this.nevisService.register(request).pipe(
      map((response): RegistrationResult => ({
        statusToken: response.statusToken,
        qrCode: { dataUri: response.qrCode.dataUri },
        isRegistered: false,
        isPending: true
      }))
    );
  }

  // TODO 19-Aug-2026/tabe: error states need to be checked
  pollRegistrationStatus(registration: RegistrationResult): Observable<RegistrationResult> {
    return this.getRegistrationStatus(registration.statusToken).pipe(
      exhaustMap((response) => this.handleStatusResponse(response, registration)),
      repeat({
        count: this.maxStatusChecksBeforeTimeout,
        delay: this.statusCheckInterval
      }),
      takeWhile((result) => !!result.isPending, true),
      last(),
      map((result) =>
        result.isPending
          ? {
              ...registration,
              isPending: false,
              isRegistered: false,
              isCanceled: false,
              isServerError: false,
              isTimedOut: true
            }
          : result
      ),
      catchError(() =>
        of({
          ...registration,
          isPending: false,
          isRegistered: false,
          isCanceled: false,
          isServerError: true,
          isTimedOut: false
        })
      )
    );
  }

  getRegistrationStatus(
    statusToken: string,
    appStatus: string = authenticationApi.NevisStatusRequest.AppStatusEnum.NA
  ): Observable<HttpResponse<authenticationApi.NevisStatusResponse>> {
    return this.nevisService.status(
      {
        statusToken,
        appStatus: appStatus as any
      },
      'response'
    );
  }

  private handleStatusResponse(
    response: HttpResponse<authenticationApi.NevisStatusResponse>,
    registrationResult: RegistrationResult
  ): Observable<RegistrationResult> {
    if (response.status !== HttpStatusCode.Ok) {
      return of({
        ...registrationResult,
        isPending: false,
        isRegistered: false,
        isServerError: true,
        isCanceled: true
      });
    }
    const data = response.body;
    const isRegistered = data?.status === authenticationApi.NevisStatusResponse.StatusEnum.SUCCEEDED;
    return of({
      ...registrationResult,
      isRegistered,
      isCanceled: data.status === authenticationApi.NevisStatusResponse.StatusEnum.FAILED,
      isPending: data.status === authenticationApi.NevisStatusResponse.StatusEnum.PENDING
    });
  }
}
